import { file } from "jszip";

// callgraph.js
export class CallGraph {
    constructor({showRecursion=false, maxPathDepth=1, minTotalMs=0} = {}) {
        this.enabled = false;


        this.reset();
        
        this.showRecursion = showRecursion;
        this.maxPathDepth = maxPathDepth || (showRecursion ? 0 : 1);
        this.minTotalMs = minTotalMs;
    }

    start() {
        if (this.enabled) return;
        this.enabled = true;
        // (опционально) запомнить t0, очистить предыдущие edge-накопления и т.п.
        // this.t0 = performance.now();
        this.reset();
        this.startTime = performance.now();
    }

    stop() {
        if (!this.enabled) return;
        this.enabled = false;
        this.endTime = performance.now();
        const duration = ((this.endTime - this.startTime) / 1000).toFixed(3);
        // console.log(`[CallGraph] Stopped profiling after ${duration}s`);
    }

    reset() {
        this.nodes = new Map();               // key -> node
        this.parents = new Map();             // childKey -> Set(parentKeys)
        this.stack = [];                      // { key, start, childNs }

        this.keysStr_from_keysStr0 = new Map();
        this.ikeyStr = 0
    }

    _nowNs() {
        // perf.now в мс с высокой точностью -> ns
        return performance.now() * 1e6;
    }

    _key0(label, meta) {
        // label: 'Comp.method' / 'store:cart/addItem' / 'fn:parseData'
        // meta: {file,line} опционально
        return { label, ...meta} //, file: meta?.file, line: meta?.line };
    }

    _key(key0) {
        // вернём и массив, и строку
        const prevArr = this.stack[this.stack.length-1]?.keyArr || [];
        let arr = prevArr.length ? [...prevArr, key0] : [key0];
        if (this.maxPathDepth && arr.length > this.maxPathDepth) arr = arr.slice(-this.maxPathDepth);

        // компактная строка без JSON (минимум сборки мусора):
        // используем label|file|line как токен звена
        const keyStr0 = `${key0.label}|${key0.widget_id||''}|${key0.binding||''}`;
        let keyStr = this.keysStr_from_keysStr0.get(keyStr0)
        if (!keyStr) {
            keyStr = `${++this.ikeyStr}`
            this.keysStr_from_keysStr0.set(keyStr0, keyStr)
        }
        
        const str = prevArr.length ? `${this.stack[this.stack.length-1].keyStr}:${keyStr}` : keyStr;

        return { arr, str };
    }

    _getNode(keyStr, lastSeg) {
        let n = this.nodes.get(keyStr);
        if (!n) {
            const last = lastSeg; // уже есть, не нужно парсить
            n = {
                label: last.label,
                name: last.name,
                widget_id: last.widget_id || '',
                binding: last.binding || '',
                file: last.file || '',
                line: last.line || 0,
                calls: 0,
                totalNs: 0,
                selfNs: 0,
                children: new Map(),
                childTotals: new Map(),
            };
            this.nodes.set(keyStr, n);
        }
        return n;
    }

    callSub(name) {
        if (!this.enabled) return;
        if (!this.stack.length) return;
        const parent = this.stack[this.stack.length - 1];
        
        const file = parent.lastSeg.label.replace(('.', ':'));
        const label = file + '.' + name;
        this.callStart(label, {...parent.lastSeg, label, name, file});
    }

    callStart(label, meta) {
        if (!this.enabled) return;
        const key0 = this._key0(label, meta);
        const { arr, str } = this._key(key0);
        this.stack.push({ keyArr: arr, keyStr: str, lastSeg: key0, start: this._nowNs(), childNs: 0 });
    }

    callEnd(result) {
        if (!this.enabled) return;
        if (!this.stack.length) return;
        const fr = this.stack.pop();
        if (!fr) return;
        const dur = this._nowNs() - fr.start;

        const node = this._getNode(fr.keyStr, fr.lastSeg);
        node.calls += 1;
        node.totalNs += dur;
        node.selfNs  += Math.max(0, dur - fr.childNs);

        if (this.stack.length) {
            const parent = this.stack[this.stack.length - 1];
            if (!parent) return;
            parent.childNs += dur;

            // const sameFunc =
            // parent.lastSeg.label === fr.lastSeg.label &&
            // (parent.lastSeg.file||'') === (fr.lastSeg.file||'') &&
            // (parent.lastSeg.line||0)  === (fr.lastSeg.line||0);

            // const show = this.showRecursion || !sameFunc;
            // if (show) {
                const pnode = this._getNode(parent.keyStr, parent.lastSeg);
                pnode.children.set(fr.keyStr, (pnode.children.get(fr.keyStr) || 0) + 1);
                pnode.childTotals.set(fr.keyStr, (pnode.childTotals.get(fr.keyStr) || 0) + dur);
                if (!this.parents.has(fr.keyStr)) this.parents.set(fr.keyStr, new Set());
                this.parents.get(fr.keyStr).add(parent.keyStr);
            // }
        }
    }

    setTimeout(vueObj, label, fn, delay, meta) {
        // return window.setTimeout(fn, delay);
        
        const parts = label.split('.');
        meta = {
            file: parts[0],
            name: `🕒${parts[1]}`,
            widget_id: vueObj?.widget_id||'',
            binding: vueObj?.attr?.binding||'',
            ...meta
        };
        return window.setTimeout(this.bind(`${meta.file}.${meta.name}`, fn, meta), delay);

        // Example:
        // WIDGET.timerId = this.$callgraph.setTimeout(
        //     'AttrGrid.widgetResize_afterTimer',
        //     () => store.widgetResize_afterTimer(vueObj),
        //     timeout
        // );
    }

    // Временно выполнить fn "будто бы" внутри конкретного родителя
    _runWithParent(parentTop, fn) {
        this.stack.push(parentTop);

        try {
            return fn();
        } finally {
            this.stack.pop();          // просто убрать ghost-кадр
        }
    }

    // Обёртка для асинхронных вызовов (таймеры/промисы/события)
    bind(label, fn, meta) {
        const cg = this;

        // Сохраняем родителя на момент планирования
        let parentTop
        if (this.stack.length) {
            parentTop = this.stack[this.stack.length - 1]
        }

        // Используем уже существующий wrap, чтобы замерять сам fn
        const wrapped = wrap(cg, label, fn, meta);

        const _key0 = this._key0.bind(this),
        _key = this._key.bind(this),
        _nowNs = this._nowNs.bind(this);
        return function boundWrapper(...args) {
            if (!cg.enabled) return fn.apply(this, args);

            // В момент выполнения (через timeout) стек пуст,
            // поэтому "подкладываем" сохранённого родителя
            return cg._runWithParent(parentTop, () => wrapped.apply(this, args));
        };
    }

    // красивый вывод дерева (в мс)
    render_tree({unit_sec=1e-3, max_children=20, max_depth=20} = {}) {
        const fmt = ns => (ns * 1e-9) / unit_sec;
        const fmtNum = (v, w=10) => {
        const iv = Math.abs(v - Math.trunc(v)) < 1e-9 ? Math.trunc(v) : Math.round(v*1000)/1000;
        const s = String(iv);
        return s.padStart(w, ' ');
        };

        const all = new Set(this.nodes.keys());
        const childs = new Set(this.parents.keys());
        const roots = [...all].filter(k => !childs.has(k))
            .sort((a,b) => this.nodes.get(b).totalNs - this.nodes.get(a).totalNs);

        const lines = [];
        lines.push('='.repeat(80));
        lines.push('CALL TREE (sorted by total time desc)');
        lines.push(`${'Total'}  ${'Self'}  ${'Calls'}  Function`);
        lines.push('-'.repeat(80));

        const call_tree = []; const agg = {}

        const dump = (keyStr, prefix='', isLast=true, depth=0, profiling_parent=null, parentKeyStr=null) => {
            if (depth > max_depth) return;
            const node = this.nodes.get(keyStr);
            if (!node) return;
            // if (node.calls === 0 || node.totalNs === 0) return;

            let total, self, calls;
            if (!parentKeyStr || this.maxPathDepth !== 1) {
                total = fmt(node.totalNs);
                self  = fmt(node.selfNs);
                calls = node.calls;
            } else {
                const pnode = this.nodes.get(parentKeyStr);
                total = fmt(pnode.childTotals.get(keyStr) || 0);
                self  = 0;
                calls = pnode.children.get(keyStr) || 0;
            }
            if (this.minTotalMs && total < this.minTotalMs) return;

            const joint = isLast ? '└─ ' : '├─ ';
            lines.push( `${fmtNum(total)}  ${fmtNum(self)}  ${String(calls).padStart(7,' ')}  ${prefix}${joint}${node.name}  ${node.widget_id||''}  ${node.binding||''}` );
            
            const profiling_row = {
                "keyStr": keyStr,
                "name": node.name,
                "file": node.file,
                "line": node.line,
                
                "widget_id": node.widget_id,
                "binding": node.binding,

                "total_ms": total,
                "self_ms": self,
                "calls": calls,
            }
            profiling_parent.push(profiling_row)
            
            // aggregation
            const key_acc = (node.label)
            const acc = agg[key_acc] = agg[key_acc] || {
                "name": node.name,
                "file": node.file,
                "line": node.line,
                "total_ms": 0.0,
                "self_ms": 0.0,
                "calls": 0,
            }
            acc["total_ms"] += profiling_row["total_ms"]
            acc["self_ms"]  += profiling_row["self_ms"]
            acc["calls"]    += profiling_row["calls"]

            // children
            const children = [...(node.children.keys())]
                .sort((a,b) => (node.childTotals.get(b)||0) - (node.childTotals.get(a)||0))
                .slice(0, max_children);

            for (let i=0;i<children.length;i++){
                const ck = children[i];
                const last = i === children.length - 1;
                const childPrefix = prefix + (isLast ? '   ' : '│  ');
                profiling_row.attrs = profiling_row.attrs || []
                dump(ck, childPrefix, last, depth+1, profiling_row.attrs, keyStr);
            }
        };

        for (let i=0;i<roots.length;i++){
            dump(roots[i], '', i===roots.length-1, 0, call_tree, null);
        }

        lines.push('='.repeat(80));
        const call_summary = Object.values(agg).sort((a, b) => b.total_ms - a.total_ms);
        
        return {
            call_tree_text:lines.join('\n'),
            call_tree,
            call_summary,
        };
    }
}

// универсальный враппер функции
export function wrap(cg, label, fn, meta) {
    return function wrapped(...args) {
        if (!cg.enabled) {
            return fn.apply(this, args);
        }
        // const label = typeof labelOrFn === 'function' ? labelOrFn.call(this, ...args) : labelOrFn;
        cg.callStart(label, meta);
        try {
            const r = fn.apply(this, args);
            if (r && typeof r.then === 'function') {
                return r.finally(() => cg.callEnd());
            }
            cg.callEnd();
            return r;
        } catch (e) {
            cg.callEnd();
            throw e;
        }
    };
}